import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { churchToday, formatDate } from '@/utils/format';

import { PastoralSubnav } from './pastoral-subnav';
import { useMemberPicker } from './use-member-picker';

interface CounselingRow {
  id: string;
  memberId: string;
  counselorMemberId: string;
  sessionDate: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  topic: string | null;
  followUpNeeded: boolean;
}

export const CounselingPage = () => {
  const { t } = useTranslation('pastoral');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canRecord = hasPermission(membership, PERMISSIONS.PASTORAL_RECORD_COUNSELING);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [open, setOpen] = useState(false);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [counselorMemberId, setCounselorMemberId] = useState<string | null>(null);
  const [sessionDate, setSessionDate] = useState<string | null>(churchToday());
  const [topic, setTopic] = useState('');
  const [notes, setNotes] = useState('');

  const memberPicker = useMemberPicker(open, 'pastoral-counseling-member');
  const counselorPicker = useMemberPicker(open, 'pastoral-counseling-counselor');

  const list = useQuery({
    queryKey: ['pastoral', 'counseling', page, pageSize],
    queryFn: () =>
      api.get<{ data: CounselingRow[]; page: OffsetPage }>('/pastoral/counseling', {
        query: { page, pageSize },
      }),
    placeholderData: keepPreviousData,
  });

  const reset = () => {
    setMemberId(null);
    setCounselorMemberId(null);
    setSessionDate(churchToday());
    setTopic('');
    setNotes('');
    memberPicker.setSelected(null);
    counselorPicker.setSelected(null);
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<CounselingRow>('/pastoral/counseling', {
        memberId,
        counselorMemberId,
        sessionDate,
        topic: topic.trim() || null,
        notes: notes.trim() || null,
        status: 'completed',
      }),
    onSuccess: async () => {
      toast.success(t('counseling.saved'));
      setOpen(false);
      reset();
      await queryClient.invalidateQueries({ queryKey: ['pastoral', 'counseling'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const columns: DataTableColumn<CounselingRow>[] = [
    {
      id: 'sessionDate',
      header: t('counseling.columns.sessionDate'),
      cell: (row) => formatDate(row.sessionDate),
    },
    {
      id: 'topic',
      header: t('counseling.columns.topic'),
      cell: (row) => row.topic || '—',
    },
    {
      id: 'status',
      header: t('counseling.columns.status'),
      cell: (row) => (
        <Badge variant="neutral">{t(`counseling.status.${row.status}`)}</Badge>
      ),
    },
    {
      id: 'followUp',
      header: t('counseling.columns.followUp'),
      cell: (row) =>
        row.followUpNeeded ? (
          <Badge variant="warning">{t('counseling.followUp.needed')}</Badge>
        ) : (
          t('counseling.followUp.none')
        ),
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;
  const canSubmit = Boolean(memberId && counselorMemberId && sessionDate);

  return (
    <div>
      <PageHeader
        title={t('counseling.title')}
        description={t('counseling.description')}
        headerActions={
          canRecord ? (
            <Button onClick={() => setOpen(true)}>{t('counseling.create')}</Button>
          ) : null
        }
      />

      <PastoralSubnav />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={
          <div className="space-y-3 py-4 text-center">
            <p className="text-body font-medium">{t('counseling.empty.title')}</p>
            <p className="text-body-sm text-fg-secondary">{t('counseling.empty.description')}</p>
            {canRecord ? (
              <div className="flex justify-end sm:justify-center">
                <Button onClick={() => setOpen(true)}>{t('counseling.empty.action')}</Button>
              </div>
            ) : null}
          </div>
        }
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <FormDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) reset();
        }}
        title={t('counseling.create')}
        submitLabel={t('counseling.save')}
        cancelLabel={tCommon('action.cancel')}
        submitting={create.isPending}
        submitDisabled={!canSubmit}
        size="lg"
        onSubmit={() => create.mutate()}
      >
        <FormField label={t('counseling.fields.member')} required>
          {(field) => (
            <EntityCombobox
              id={field.id}
              value={memberId}
              onChange={(id) => {
                setMemberId(id);
                memberPicker.select(id);
              }}
              options={memberPicker.options}
              selected={memberPicker.selected}
              onQueryChange={memberPicker.setQuery}
              loading={memberPicker.loading}
              placeholder={t('memberPicker.placeholder')}
              emptyMessage={t('memberPicker.empty')}
              clearLabel={tCommon('action.clearSelection')}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('counseling.fields.counselor')} required>
          {(field) => (
            <EntityCombobox
              id={field.id}
              value={counselorMemberId}
              onChange={(id) => {
                setCounselorMemberId(id);
                counselorPicker.select(id);
              }}
              options={counselorPicker.options}
              selected={counselorPicker.selected}
              onQueryChange={counselorPicker.setQuery}
              loading={counselorPicker.loading}
              placeholder={t('memberPicker.placeholder')}
              emptyMessage={t('memberPicker.empty')}
              clearLabel={tCommon('action.clearSelection')}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('counseling.fields.sessionDate')} required>
          {(field) => (
            <DatePicker
              id={field.id}
              value={sessionDate}
              onChange={setSessionDate}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('counseling.fields.topic')}>
          {(field) => (
            <Input
              id={field.id}
              value={topic}
              onChange={(event) => setTopic(event.target.value)}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('counseling.fields.notes')}>
          {(field) => (
            <Textarea
              id={field.id}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={4}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>
      </FormDialog>
    </div>
  );
};
